/**
 * Wave 351-C · global Prumo open state.
 *
 * Reason for this store: until 351-B the inbox didn't exist, so
 * `PrumoPill` could keep `[open, setOpen] = useState(false)` locally.
 * Wave 351-B's `/v1/inbox` endpoint emits `proposal_pending` items
 * whose `primaryAction.callKind === 'open_proposal'`, and the inbox
 * UI in 351-C needs to open the existing slide-over scrolled to a
 * specific `prumoCardId`. Two callers, two open paths → state moves
 * to a tiny global store.
 *
 * Scope is intentionally minimal:
 *   - `isOpen`            — the panel is visible.
 *   - `focusedProposalId` — when set, the panel scrolls to and
 *                           highlights this proposal on open. The
 *                           panel clears the focus marker once it
 *                           has done its job (see PrumoQueue).
 *
 * The legacy `useUi.prumoOpen` mirror still gets updated by
 * `PrumoQueue` for z-index coordination with the Housy bubble +
 * onboarding tour overlays — that contract isn't broken here.
 *
 * Why a separate store from `useUi`:
 *   - `useUi.prumoOpen` is a *side-channel* read for chrome that
 *     wants to step out of Prumo's way. The new openPrumo() / focus
 *     contract is *imperative* and proposal-aware. Mixing them
 *     turns one mechanic ("are you visible?") into another ("which
 *     card do you want me to scroll to?") and tangles three
 *     overlays together.
 */
import { create } from 'zustand';

interface PrumoState {
  /** Slide-over panel is visible. */
  isOpen: boolean;
  /**
   * When set, the next render of `PrumoQueue` should scroll into
   * view and visually highlight the matching `pm_proposals.id`.
   * `null` means "open the panel without focusing any specific
   * card" — the legacy pill-click behavior.
   */
  focusedProposalId: string | null;
  /**
   * Open the panel, optionally focused on a specific proposal id.
   * If the panel is already open and a different id is passed, the
   * focus marker updates so the panel scrolls to the new card.
   */
  openPrumo: (proposalId?: string | null) => void;
  /** Close the panel. Clears the focus marker as a side effect. */
  closePrumo: () => void;
  /**
   * Called by `PrumoQueue` after it finishes scrolling to the
   * focused card so a re-open without an explicit id doesn't
   * re-trigger the highlight from a stale value.
   */
  consumeFocus: () => void;
}

export const usePrumo = create<PrumoState>((set) => ({
  isOpen: false,
  focusedProposalId: null,
  openPrumo: (proposalId = null) =>
    set({ isOpen: true, focusedProposalId: proposalId ?? null }),
  closePrumo: () => set({ isOpen: false, focusedProposalId: null }),
  consumeFocus: () => set({ focusedProposalId: null }),
}));
